/**
 * Forward video carousel analytics.
 * Publishes to Shopify Analytics + dataLayer (GA4/GTM).
 * Does NOT feed Tolstoy's dashboard — that only tracks Tolstoy embeds.
 */
window.FbVideoAnalytics = (function () {
  const SESSION_VIEWERS = 'fb_video_viewers';
  const SESSION_IMPRESSIONS = 'fb_video_impressions';
  const SESSION_INTERACTIONS = 'fb_video_interactions';
  const SESSION_ATTRIBUTION = 'fb_video_attribution';

  function readSet(key) {
    try {
      return new Set(JSON.parse(sessionStorage.getItem(key) || '[]'));
    } catch {
      return new Set();
    }
  }

  function writeSet(key, set) {
    try {
      sessionStorage.setItem(key, JSON.stringify([...set]));
    } catch (_) {}
  }

  function basePayload(meta) {
    return {
      widget: 'forward_video_carousel',
      sectionId: meta.sectionId || '',
      blockId: meta.blockId || '',
      videoTitle: meta.videoTitle || '',
      videoId: meta.videoId || '',
      pageUrl: window.location.href,
      pageType: meta.pageType || '',
      device: window.innerWidth < 750 ? 'mobile' : 'desktop',
    };
  }

  function publish(name, meta, extra) {
    if (meta.enabled === false) return;

    const payload = { ...basePayload(meta), ...extra };

    if (window.Shopify?.analytics?.publish) {
      try {
        Shopify.analytics.publish(name, payload);
      } catch (_) {}
    }

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: name, ...payload });

    document.dispatchEvent(
      new CustomEvent('fb-video-analytics', { detail: { name, payload } })
    );
  }

  function oncePerSession(key, id, fn) {
    const set = readSet(key);
    if (set.has(id)) return false;
    set.add(id);
    writeSet(key, set);
    fn();
    return true;
  }

  function storeAttribution(meta) {
    try {
      sessionStorage.setItem(
        SESSION_ATTRIBUTION,
        JSON.stringify({
          videoTitle: meta.videoTitle,
          blockId: meta.blockId,
          sectionId: meta.sectionId,
          at: Date.now(),
        })
      );
    } catch (_) {}
  }

  return {
    impression(meta) {
      const id = meta.blockId || meta.videoTitle;
      oncePerSession(SESSION_IMPRESSIONS, id, () => {
        publish('fb_video_impression', meta, { metric: 'unique_impression' });
      });
    },

    interaction(meta, interactionType) {
      const id = `${meta.blockId}:${interactionType}`;
      oncePerSession(SESSION_INTERACTIONS, id, () => {
        publish('fb_video_interaction', meta, { interactionType });
      });
      publish('fb_video_interaction_raw', meta, { interactionType });
    },

    tilePlay(meta) {
      this.interaction(meta, 'tile_play');
      publish('fb_video_tile_play', meta, {});
    },

    viewerOpen(meta) {
      const id = meta.blockId || meta.videoTitle;
      const isNewViewer = oncePerSession(SESSION_VIEWERS, id, () => {
        publish('fb_video_viewer', meta, { metric: 'unique_viewer' });
      });
      if (isNewViewer) storeAttribution(meta);
      publish('fb_video_viewer_open', meta, { isNewViewer });
      this.interaction(meta, 'viewer_open');
    },

    viewerClose(meta, durationMs) {
      publish('fb_video_viewer_close', meta, { durationMs: durationMs || 0 });
    },

    productClick(meta, product) {
      storeAttribution(meta);
      publish('fb_video_product_click', meta, {
        productId: product.productId,
        productTitle: product.title,
        productUrl: product.url,
        metric: 'product_ctr',
      });
      this.interaction(meta, 'product_click');
    },

    addToCart(meta, product, cartLine) {
      storeAttribution(meta);
      const price = cartLine?.price ? cartLine.price / 100 : 0;
      publish('fb_video_add_to_cart', meta, {
        productId: product.productId,
        variantId: product.variantId,
        productTitle: product.title,
        value: price,
        currency: window.Shopify?.currency?.active || 'USD',
        metric: 'viewer_revenue_signal',
      });
      this.interaction(meta, 'add_to_cart');
    },

    cartLineItemProperties(meta) {
      if (!meta.videoTitle) return {};
      return {
        _Video_Story: meta.videoTitle,
        _Video_Widget: 'Forward Video Carousel',
      };
    },
  };
})();
